// #tag: OPPORTUNITY TRACKING HOOK
// #purpose: Syncs candidate tracked opportunity status with backend subscriptions and provides toggle actions.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthProvider';
import { fetchTrackedOpportunities, trackOpportunity, untrackOpportunity } from '../api/opportunities';

export function useTrackedOpportunities() {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['opportunities', 'tracked'],
    queryFn: fetchTrackedOpportunities,
    enabled: isSignedIn,
  });

  const trackedIds = new Set((data?.data ?? []).map((row) => row.opportunityId));

  const trackMutation = useMutation({
    mutationFn: (opportunityId: string) => trackOpportunity(opportunityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunities', 'tracked'] }),
  });

  const untrackMutation = useMutation({
    mutationFn: (opportunityId: string) => untrackOpportunity(opportunityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opportunities', 'tracked'] }),
  });

  return {
    isTracked: (id: string) => trackedIds.has(id),
    track: (id: string) => trackMutation.mutateAsync(id),
    untrack: (id: string) => untrackMutation.mutateAsync(id),
    isPending: trackMutation.isPending || untrackMutation.isPending,
    tracked: data?.data ?? [],
  };
}
